// ./src/components/features/Dashboard/DashboardTopForms.tsx
import React from "react";
import {
  Paper,
  Title,
  Text,
  Group,
  Table,
  Badge,
  Box,
  Tooltip,
} from "@mantine/core";
import { STATUSES } from "@sdflc/utils";

import { AppButton, EmptyState } from "@/components/generic";
import { DashboardTopForm } from "@/utils/api";

// Helper to format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

export interface DashboardTopFormsProps {
  topForms: DashboardTopForm[];
  onNavigateToForm: (formId: string) => void;
  onNavigateToForms: () => void;
  onCreateForm: () => void;
}

export const DashboardTopForms: React.FC<DashboardTopFormsProps> = ({
  topForms,
  onNavigateToForm,
  onNavigateToForms,
  onCreateForm,
}) => {
  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4} size="h5">
          Top Forms
        </Title>
        <AppButton variant="secondary" size="xs" onClick={onNavigateToForms}>
          View All
        </AppButton>
      </Group>

      {topForms.length === 0 ? (
        <EmptyState
          display
          title="No forms yet"
          description="Create your first form to start collecting submissions"
          actionLabel="Create Form"
          actionIcon="Plus"
          onAction={onCreateForm}
        />
      ) : (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Form</Table.Th>
              <Table.Th style={{ textAlign: "right" }}>Submissions</Table.Th>
              <Table.Th style={{ textAlign: "right" }}>Success</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {topForms.map((form) => (
              <TopFormRow
                key={form.id}
                form={form}
                onClick={onNavigateToForm}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
};

// Top form row component
interface TopFormRowProps {
  form: DashboardTopForm;
  onClick: (formId: string) => void;
}

const TopFormRow: React.FC<TopFormRowProps> = ({ form, onClick }) => {
  const hasLimit = form.submissionsQtyLimit && form.submissionsQtyLimit > 0;
  const limitPercent = hasLimit
    ? (form.submissionsCount / form.submissionsQtyLimit!) * 100
    : 0;

  return (
    <Table.Tr style={{ cursor: "pointer" }} onClick={() => onClick(form.id)}>
      <Table.Td>
        <Group gap="xs">
          <Box>
            <Text size="sm" fw={500}>
              {form.name}
            </Text>
            <Text size="xs" c="dimmed">
              /{form.path}
            </Text>
          </Box>
          {form.status !== STATUSES.ACTIVE && (
            <Badge size="xs" color="gray" variant="light">
              Inactive
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td style={{ textAlign: "right" }}>
        <Group gap={4} justify="flex-end">
          <Text size="sm">{formatNumber(form.submissionsThisPeriod)}</Text>
          {hasLimit && (
            <Tooltip
              label={`${form.submissionsCount} / ${form.submissionsQtyLimit} limit`}
            >
              <Badge
                size="xs"
                color={limitPercent >= 80 ? "orange" : "gray"}
                variant="light"
              >
                {Math.round(limitPercent)}%
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td style={{ textAlign: "right" }}>
        <Text size="sm" c={form.successRate < 70 ? "orange" : undefined}>
          {form.successRate}%
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};
